export type AnalyticsEventName =
  | "tool_started"
  | "font_selected"
  | "template_selected"
  | "bio_copied"
  | "example_copied"
  | "copy_failed";

export type AnalyticsParameters = Record<string, string | number | boolean>;

type AnalyticsTarget = {
  gtag?: (
    command: "event",
    eventName: AnalyticsEventName,
    parameters: AnalyticsParameters
  ) => void;
};

type AnalyticsDispatcher = (
  eventName: AnalyticsEventName,
  parameters: AnalyticsParameters
) => boolean;

type PreviewCopyEventInput = {
  copySource: "preview";
  inputText: string;
  outputText: string;
  fontStyle: string;
  templateGroup: string;
  pageLanguage?: string;
};

type ExampleCopyEventInput = {
  copySource: "platform_example" | "style_example";
  outputText: string;
  exampleGroup?: string;
  pageLanguage?: string;
};

type CopySuccessEventInput = PreviewCopyEventInput | ExampleCopyEventInput;

type CopySuccessEvent = {
  eventName: "bio_copied" | "example_copied";
  parameters: AnalyticsParameters;
};

const getBrowserAnalyticsTarget = (): AnalyticsTarget | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window as Window & AnalyticsTarget;
};

export function trackAnalyticsEvent(
  eventName: AnalyticsEventName,
  parameters: AnalyticsParameters = {},
  target: AnalyticsTarget | undefined = getBrowserAnalyticsTarget()
): boolean {
  if (typeof target?.gtag !== "function") {
    return false;
  }

  try {
    target.gtag("event", eventName, parameters);
    return true;
  } catch {
    return false;
  }
}

export function createOneTimeAnalyticsEvent(
  eventName: AnalyticsEventName,
  parameters: AnalyticsParameters,
  dispatch: AnalyticsDispatcher = trackAnalyticsEvent
): () => boolean {
  let sent = false;

  return () => {
    if (sent) {
      return false;
    }

    sent = dispatch(eventName, parameters);
    return sent;
  };
}

export function buildCopySuccessEvent(
  input: CopySuccessEventInput
): CopySuccessEvent {
  if (input.copySource === "preview") {
    return {
      eventName: "bio_copied",
      parameters: {
        copy_source: input.copySource,
        font_style: input.fontStyle,
        template_group: input.templateGroup,
        input_length: input.inputText.trim().length,
        output_length: input.outputText.length,
        page_language: input.pageLanguage ?? "en"
      }
    };
  }

  return {
    eventName: "example_copied",
    parameters: {
      copy_source: input.copySource,
      example_group: input.exampleGroup ?? "unknown",
      output_length: input.outputText.length,
      page_language: input.pageLanguage ?? "en"
    }
  };
}
